import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  BookOpen,
  TrendingDown,
  BellOff,
  ClipboardList,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader } from '../components/loader';
import NotificationModal from '../components/notification-modal';
import { useWorkspace } from '../context/workspace-context';
import {
  useNotifications,
  useMarkAsRead,
  type Notification,
} from '../hooks/use-notifications';

const NOTIFICATION_ICON: Record<string, typeof Bell> = {
  custom: Bell,
  course_published: BookOpen,
  leaderboard_drop: TrendingDown,
  test_reminder: ClipboardList,
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);

  const { data, isLoading, error, fetchNextPage, hasNextPage } =
    useNotifications(currentWorkspace?.id);

  const markAsRead = useMarkAsRead();

  const handleNotificationClick = (notification: Notification) => {
    setSelectedNotification(notification);

    if (!notification.isRead) {
      markAsRead.mutate(notification.id);
    }
  };

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <div className="flex flex-col min-h-screen max-w-lg mx-auto bg-bg-main px-4 py-6">
        <h1 className="text-xl font-bold text-incorrect mb-4">Error</h1>
        <p className="text-title mb-2">
          Failed to load notifications. Please try again.
        </p>
        <p className="text-sm text-subheading">{(error as Error).message}</p>
        <button
          onClick={() => navigate('/courses')}
          className="mt-4 bg-accent-purple text-white px-4 py-2 rounded-lg"
        >
          Back to Courses
        </button>
      </div>
    );
  }

  const notifications = data?.pages.flatMap((page) => page.notifications) ?? [];

  return (
    <div className="flex flex-col min-h-screen max-w-lg mx-auto bg-bg-main px-4 py-6">
      <header className="sticky top-0 z-10 bg-bg-main pb-2 mb-4">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/courses')}
            className="text-title hover:text-accent-purple transition-colors mr-4"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold text-title">Notifications</h1>
        </div>
      </header>

      <main className="flex-1">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <BellOff className="h-8 w-8 text-gray-300" />
            </div>
            <h2 className="text-base font-semibold text-title mb-1">
              No notifications yet
            </h2>
            <p className="text-sm text-subheading">
              You'll see updates here when there's something new.
            </p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={notifications.length}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<Loader />}
          >
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const Icon = NOTIFICATION_ICON[notification.type] ?? Bell;

                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="w-full text-left flex items-start gap-3 py-4 transition-colors"
                  >
                    <div
                      className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        notification.isRead
                          ? 'bg-gray-100'
                          : 'bg-accent-purple/15'
                      }`}
                    >
                      <Icon
                        className={`h-4.5 w-4.5 ${
                          notification.isRead
                            ? 'text-subheading'
                            : 'text-accent-purple'
                        }`}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3
                          className={`text-sm leading-snug ${
                            notification.isRead
                              ? 'font-medium text-title'
                              : 'font-bold text-title'
                          }`}
                        >
                          {notification.title}
                        </h3>
                        {!notification.isRead && (
                          <span className="mt-1.5 h-2 w-2 rounded-full bg-accent-purple shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-subheading line-clamp-2 mt-0.5">
                        {notification.text}
                      </p>
                      <span className="text-xs text-subheading/70 mt-1 block">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </InfiniteScroll>
        )}
      </main>

      {selectedNotification && (
        <NotificationModal
          notification={selectedNotification}
          onClose={() => setSelectedNotification(null)}
        />
      )}
    </div>
  );
};

export default NotificationsPage;
